import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import * as RecordingActions from '../actions/recording.actions';
import * as RecordingReducer from '../reducers/recording.reducer';
import * as RecordingSelectors from '../selectors/recording.selectors';
import {
  CombinedLongeingData,
  LongeingDirectionType,
  Recording,
  RecordingUpdate,
} from '@sleipai/sleip-models';
import { Observable } from 'rxjs';
import { QueryConfig } from '../../helpers/query-config';
import { RecordingService } from '../services/recording.service';

@Injectable()
export class RecordingStoreFacade {
  constructor(private readonly store: Store<RecordingReducer.State>) {}

  private recordingService = inject(RecordingService);

  nextRecordingPageList$ = (dbStatus: string) =>
    this.store.pipe(
      select(RecordingSelectors.selectRecordingsByType(dbStatus))
    );

  currentRecordingEntityPageLoaded$ = (dbStatus: string) =>
    this.store.pipe(
      select(RecordingSelectors.selectRecordingsByType(dbStatus)),
      map(
        (recordings: Recording[]) =>
          recordings !== null && recordings.length !== 0
      )
    );

  getNextRecordingEntityPageList(
    status: string,
    dbStatus: string,
    offset?: any
  ) {
    return this.store.dispatch(
      RecordingActions.loadRecordingListByType({
        status: status,
        dbStatus: dbStatus,
        offset: offset,
      })
    );
  }

  getIsLoading$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectIsLoading)
  );

  getIsListLoading$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectIsListLoading)
  );

  getIsListCountLoading$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectIsListCountLoading)
  );

  fetchRecordingsCount$ = (horseId?: string, filterMap?: Map<string, any>) => {
    this.store.dispatch(
      RecordingActions.loadRecordingsCountInFirestoreDb({ horseId, filterMap })
    );
  };

  fetchRecordingsListPage$ = (queryConfig: QueryConfig, horseId?: string) => {
    return this.store.dispatch(
      RecordingActions.loadRecordingsListInFirestoreDb({
        queryConfig,
        horseId,
      })
    );
  };

  getTotalRecordingsCount$: Observable<number> = this.store.pipe(
    select(RecordingSelectors.selectTotalRecordingsCount)
  );

  getRecordingsListPage$ = (queryConfig: QueryConfig, horseId?: string) => {
    return this.store.pipe(
      select(RecordingSelectors.selectRecordingsListPage(queryConfig, horseId))
    );
  };

  getRecordingById$ = (recordingId?: string) => {
    return this.store.pipe(
      select(RecordingSelectors.selectSingleRecording(recordingId))
    );
  };

  getMultipleRecordingsByIds$ = (recordingIds: string[]) => {
    return this.store.pipe(
      select(RecordingSelectors.selectMultipleRecordings(recordingIds))
    );
  };

  getRecordingError$: Observable<string | null> = this.store.pipe(
    select(RecordingSelectors.selectRecordingError)
  );

  removeRecording$ = (recordingId: string) => {
    return this.store.dispatch(
      RecordingActions.removeRecording({ recordingId })
    );
  };

  loadSingleRecording$ = (id: string) => {
    return this.store.dispatch(RecordingActions.loadSingleRecording({ id }));
  };

  getRecordingRemoved$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectRecordingRemoved)
  );

  // Infinite recording list pagination start
  fetchRecordingsInfiniteScrollListPageForHorse$ = (
    horseId: string,
    queryConfig: QueryConfig
  ) => {
    this.store.dispatch(
      RecordingActions.loadRecordingsInfiniteScrollListPageForHorse({
        horseId,
        queryConfig,
      })
    );
  };

  //// Dashboard 2.0 recordings list pagination start ////
  fetcRecordingsListForInfiniteScrollInDashBoard$ = (
    queryConfig: QueryConfig,
    reset: boolean
  ) => {
    return this.store.dispatch(
      RecordingActions.loadRecordingsListForInfiniteScrollInDashBoard({
        queryConfig,
        reset,
      })
    );
  };

  getRecordingsListForInfiniteScrollInDashBoard$ = (
    queryConfig: QueryConfig
  ) => {
    return this.store.select(
      RecordingSelectors.selectRecordingsListPageForInfiniteScrollInDashBoard(
        queryConfig
      )
    );
  };

  fetchRecordingsCountForInfiniteScrollInDashBoard$ = (
    queryConfig: QueryConfig,
    reset: boolean
  ) => {
    return this.store.dispatch(
      RecordingActions.loadRecordingsCountForInfiniteScrollInDashBoard({
        queryConfig,
        reset,
      })
    );
  };

  getRecordingsCountForInfiniteScrollInDashBoard$ = this.store.select(
    RecordingSelectors.selectTotalRecordingsCount
  );

  getHasMoreRecordings$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectRecordingsHasMore)
  );
  //// Dashboard 2.0 recordings list pagination end ////

  // Infinite recording list pagination end

  updateRecording$ = (updatedRecordingData: RecordingUpdate) => {
    return this.store.dispatch(
      RecordingActions.updateRecordingData({ updatedRecordingData })
    );
  };

  getRecordingUpdatedFlag$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectRecordingUpdated)
  );

  getRecordingUpdatingFlag$: Observable<boolean> = this.store.pipe(
    select(RecordingSelectors.selectRecordingUpdating)
  );

  getCurrentDateRecordings$ = this.recordingService.getTodaysRecordings();

  fetchRecordingsByIds$ = (recordingIds: string[]) => {
    this.store.dispatch(
      RecordingActions.loadRecordingsByIds({
        recordingIds,
      })
    );
  };

  fetchEligibleRecordingsToLinkForARecording$ = (
    horseId: string,
    recordingId: string,
    creationDate: Date,
    longeingDirection: LongeingDirectionType
  ) => {
    return this.store.dispatch(
      RecordingActions.loadEligibleRecordingsToLinkForARecording({
        horseId,
        recordingId,
        creationDate,
        longeingDirection,
      })
    );
  };

  getEligibleRecordingsToLinkForARecording$ = (
    horseId: string,
    recordingId: string,
    creationDate: Date,
    longeingDirection: LongeingDirectionType
  ) => {
    return this.store.pipe(
      select(
        RecordingSelectors.selectEligibleRecordingsToLinkForARecording(
          horseId,
          recordingId,
          creationDate,
          longeingDirection
        )
      )
    );
  };

  IsRecordingEligibleToBeLinkedForCombinedView$ = (
    horseId: string,
    recordingId: string,
    creationDate: Date,
    longeingDirection: LongeingDirectionType
  ) => {
    return this.store.pipe(
      select(
        RecordingSelectors.selectIsRecordingEligibleToBeLinkedForCombinedView(
          horseId,
          recordingId,
          creationDate,
          longeingDirection
        )
      )
    );
  };

  getBatchRemoveFailedIds$: Observable<string[] | null> = this.store.pipe(
    select(RecordingSelectors.selectBatchRemoveFailedIds)
  );

  batchRemoveRecordings$ = (recordingIds: string[]) =>
    this.store.dispatch(RecordingActions.batchRemoveRecordings({ recordingIds }));

  getRecordingActionSuccessType$ = this.store.pipe(
    select(RecordingSelectors.selectRecordingsActionSuccessType)
  );

  getBatchMoveFailedIds$: Observable<string[] | null> = this.store.pipe(
    select(RecordingSelectors.selectBatchMoveFailedIds)
  );

  batchMoveRecordingsToHorse$ = (recordingIds: string[], horseId: string) =>
    this.store.dispatch(
      RecordingActions.batchMoveRecordingsToHorse({ recordingIds, horseId })
    );

  updateCombinedViewLinkedRecording = (
    selectedRecordingId: string,
    previousLinkedRecordingId: string | null,
    newLinkedRecordingId: string | null
  ) => {
    return this.store.dispatch(
      RecordingActions.updateCombinedViewLinkedRecording({
        selectedRecordingId,
        newLinkedRecordingId,
        previousLinkedRecordingId,
      })
    );
  };
  updateCombinedViewLinkedRecording$ = this.updateCombinedViewLinkedRecording;

  loadCombinedLongeingsForHorse = (horseId: string) => {
    return this.store.dispatch(
      RecordingActions.loadCombinedLongeingsForHorse({ horseId })
    );
  };
  loadCombinedLongeingsForHorse$ = this.loadCombinedLongeingsForHorse;

  clearCombinedLongeings = () => {
    return this.store.dispatch(RecordingActions.clearCombinedLongeings());
  };
  clearCombinedLongeings$ = this.clearCombinedLongeings;

  getAllCombinedLongeingsForHorse$: Observable<CombinedLongeingData[]> =
    this.store.pipe(select(RecordingSelectors.selectAllCombinedLongeings));

  getCombinedLongeingById$ = (
    combinedId: string
  ): Observable<CombinedLongeingData | null> => {
    return this.store.pipe(
      select(RecordingSelectors.selectCombinedLongeingById(combinedId))
    );
  };

  getCombinedLongeingByRecordingId$ = (
    recordingId: string
  ): Observable<CombinedLongeingData | null> => {
    return this.store.pipe(
      select(RecordingSelectors.selectCombinedLongeingByRecordingId(recordingId))
    );
  };
}
